/**
 * SetMerakiKeyPage — Meraki key setup + management.
 *
 * First visit (no keys): setup flow with the legacy instruction carousel.
 * After at least one key exists: manage flow with create/rename/delete and
 * active-key switching. Key material is never editable after creation.
 */
import { useEffect, useMemo, useReducer, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowRight,
  KeyRound,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  Eye,
  EyeOff,
  Pencil,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Carousel, type CarouselSlide } from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";
import { PanelSkeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { useMerakiKeys } from "@/stores/meraki-keys";
import {
  MerakiKeyMutationSchema,
  type MerakiKeyMutation,
} from "@/api/schemas/meraki";
import { ApiError } from "@/api/client";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

import step1Img from "@/assets/meraki/add-meraki-key-step1.png";
import step2Img from "@/assets/meraki/add-meraki-key-step2.png";
import step3Img from "@/assets/meraki/add-meraki-key-step3.png";
import step4Img from "@/assets/meraki/add-meraki-key-step4.png";

const SLIDES: CarouselSlide[] = [
  {
    id: "step1",
    imageSrc: step1Img,
    imageAlt: "Meraki dashboard profile menu",
    title: "Step 1 — Open your Meraki profile",
    body: "In the Meraki dashboard, click the user icon at the top right and choose your profile.",
  },
  {
    id: "step2",
    imageSrc: step2Img,
    imageAlt: "API Access section",
    title: "Step 2 — Scroll to API Access",
    body: "On the profile page, scroll down to the API Access section and generate a new API key.",
  },
  {
    id: "step3",
    imageSrc: step3Img,
    imageAlt: "Generate new key dialog",
    title: "Step 3 — Generate a new API key",
    body: "Copy the value shown, then confirm that you have stored your API key.",
  },
  {
    id: "step4",
    imageSrc: step4Img,
    imageAlt: "Paste key into Opus",
    title: "Step 4 — Paste it here",
    body: "Paste the key into the form and give it a recognizable label.",
  },
];

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }

  const data = err.data;
  if (data && typeof data === "object") {
    const maybeDetail = (data as { detail?: unknown }).detail;
    if (typeof maybeDetail === "string" && maybeDetail.trim().length > 0) {
      return maybeDetail;
    }
    const maybeMessage = (data as { message?: unknown }).message;
    if (typeof maybeMessage === "string" && maybeMessage.trim().length > 0) {
      return maybeMessage;
    }
  }

  return `API error ${err.status}`;
}

export function SetMerakiKeyPage() {
  const animatedRootRef = useUiAutoAnimate();
  const { data: keys, status, error, load } = useMerakiKeys();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");

  useEffect(() => {
    void load();
  }, [load]);

  const hasKeys = keys.length > 0;
  const activeKey = useMemo(() => keys.find((k) => k.active), [keys]);

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">
          Meraki
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">
          {hasKeys ? "Manage keys" : "Set API key"}
        </span>
      </div>

      <MerakiSubNav />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            {hasKeys ? "Manage Meraki keys" : "Set Meraki API key"}
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            {hasKeys
              ? "Create additional keys, rename labels, activate another key, or remove old keys. Secret key material cannot be edited after creation."
              : "Opus needs a Meraki dashboard key to enumerate your networks and push configuration."}
          </p>
        </div>
        <Badge variant="info" size="md">
          <ShieldCheck size={11} />
          Stored encrypted at rest
        </Badge>
      </div>

      {error ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {activeKey && redirect ? (
        <div className="flex justify-end">
          <Button asChild>
            <Link to={redirect}>Continue</Link>
          </Button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(280px,0.7fr)]">
        <MerakiKeyFormCard />
        <MerakiKeysListCard />
      </div>

      {status === "loading" && keys.length === 0 ? <PanelSkeleton /> : null}
    </div>
  );
}

function MerakiKeyFormCard() {
  const [showApiKey, setShowApiKey] = useState(false);
  const { data: keys, create, load, pollTask } = useMerakiKeys();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const hasKeys = keys.length > 0;

  type SyncState = { syncOpen: boolean; syncPct: number | undefined; syncMessage: string; syncDone: boolean };
  const [{ syncOpen, syncPct, syncMessage, syncDone }, setSync] = useReducer(
    (s: SyncState, p: Partial<SyncState>) => ({ ...s, ...p }),
    { syncOpen: false, syncPct: undefined, syncMessage: "Connecting to Meraki…", syncDone: false },
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MerakiKeyMutation>({
    resolver: zodResolver(MerakiKeyMutationSchema),
    defaultValues: { name: "", key: "", region: "global", active: true },
  });
  // eslint-disable-next-line react-hooks/incompatible-library
  const region = watch("region");

  function afterCreateSuccess() {
    if (redirect) { navigate(redirect); return; }
    void load();
  }

  async function onCreate(values: MerakiKeyMutation) {
    try {
      const taskId = await create(values);
      reset({ name: "", key: "", region: values.region, active: true });
      if (!taskId) {
        toast.success("Meraki key saved");
        afterCreateSuccess();
        return;
      }

      setSync({ syncOpen: true, syncDone: false, syncPct: undefined, syncMessage: "Associating networks to the new key…" });

      const final = await pollTask(taskId, (p) => {
        if (p.total && p.current !== undefined) {
          setSync({ syncPct: (p.current / p.total) * 100 });
        }
        if (p.message) setSync({ syncMessage: p.message });
      });

      if (final.state === "SUCCESS") {
        setSync({ syncDone: true });
        setTimeout(() => {
          setSync({ syncOpen: false, syncDone: false, syncPct: undefined });
          afterCreateSuccess();
        }, 1200);
      } else {
        toast.error("Network sync failed", {
          description: "Something went wrong. Please contact an administrator.",
        });
        setSync({ syncOpen: false });
      }
    } catch (err) {
      toast.error("Unable to create key", {
        description: getApiErrorMessage(
          err,
          "Meraki key validation failed. Please check the key and selected region.",
        ),
      });
    }
  }

  return (
    <>
      <div className="flex flex-col gap-5">
        <Card>
          <CardContent>
            <form
              noValidate
              onSubmit={(e) => void handleSubmit(onCreate)(e)}
              className="flex flex-col gap-4"
            >
              <div>
                <CardTitle>
                  {hasKeys ? "Add a new Meraki key" : "Add your first Meraki key"}
                </CardTitle>
                <CardSub>
                  New keys are validated before they are saved and set active.
                </CardSub>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="name">Label</Label>
                <Input
                  id="name"
                  placeholder={hasKeys ? "e.g. Lab · Shared" : "e.g. Eustache · personal"}
                  invalid={!!errors.name}
                  {...register("name")}
                />
                {errors.name ? (
                  <span className="text-xs text-rose">{errors.name.message}</span>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="key">API key</Label>
                <div className="relative">
                  <KeyRound
                    size={14}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
                  />
                  <Input
                    id="key"
                    type={showApiKey ? "text" : "password"}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="40-char hex key from the Meraki dashboard"
                    className="pl-9 pr-16 font-mono text-xs"
                    invalid={!!errors.key}
                    {...register("key")}
                  />
                  <button
                    type="button"
                    aria-label={showApiKey ? "Hide API key" : "Show API key"}
                    aria-pressed={showApiKey}
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 inline-flex h-7 -translate-y-1/2 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                  >
                    {showApiKey ? <EyeOff size={12} /> : <Eye size={12} />}
                    {showApiKey ? "Hide" : "Show"}
                  </button>
                </div>
                {errors.key ? (
                  <span className="text-xs text-rose">{errors.key.message}</span>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>Region</Label>
                <div className="grid grid-cols-3 gap-2">
                  {(["global", "canada", "china"] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setValue("region", r, { shouldValidate: true })}
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold capitalize transition-colors
                        ${region === r ? "border-violet/50 bg-aurora-soft text-violet" : "border-line bg-surface text-fg-dim hover:bg-surface-2 hover:text-fg"}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Validating key…" : "Save key & sync networks"}
                <ArrowRight size={14} />
              </Button>
            </form>
          </CardContent>
        </Card>

        <Carousel slides={SLIDES} className="max-w-none" />
      </div>

      <Dialog open={syncOpen} onOpenChange={(v) => setSync({ syncOpen: v })}>
        <DialogContent>
          {syncDone ? (
            <div className="flex flex-col items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mint/15 text-mint">
                <CheckCircle2 size={22} />
              </div>
              <DialogTitle>Networks synced</DialogTitle>
              <DialogDescription>
                Your key is active and all reachable networks have been associated.
              </DialogDescription>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <DialogTitle>Network sync</DialogTitle>
              <DialogDescription>{syncMessage}</DialogDescription>
              <Progress
                value={syncPct}
                hint={syncPct ? undefined : "Establishing connection…"}
              />
              <div className="text-[11px] text-fg-mute">
                Closing this dialog will not cancel the background sync.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function MerakiKeysListCard() {
  const { data: keys, activate, remove, rename } = useMerakiKeys();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState("");

  async function onActivate(id: number) {
    try {
      await activate(id);
      toast.success("Key activated");
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      toast.error("Unable to activate key", { description: msg });
    }
  }

  async function onRemove(id: number, name: string) {
    if (!window.confirm(`Remove Meraki key "${name}"?`)) return;
    try {
      await remove(id);
      toast.success(`Removed Meraki key "${name}"`);
      if (editingId === id) {
        setEditingId(null);
        setDraftName("");
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      toast.error("Unable to remove key", { description: msg });
    }
  }

  function beginRename(id: number, name: string) {
    setEditingId(id);
    setDraftName(name);
  }

  async function saveRename(id: number) {
    const next = draftName.trim();
    if (!next) {
      toast.error("Name is required");
      return;
    }
    try {
      await rename(id, next);
      toast.success("Key name updated");
      setEditingId(null);
      setDraftName("");
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      toast.error("Unable to rename key", { description: msg });
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div>
          <CardTitle>Registered keys</CardTitle>
          <CardSub>
            You can rename labels, activate another key, or remove keys.
            Secret key values are hidden after creation.
          </CardSub>
        </div>

        {keys.length === 0 ? (
          <div className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg-dim">
            No keys registered yet.
          </div>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {keys.map((k) => {
              const isEditing = editingId === k.id;
              return (
                <li
                  key={k.id}
                  className="flex items-start gap-3 rounded-lg border border-line bg-surface p-3"
                >
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      <Input
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        className="h-8"
                        autoFocus
                      />
                    ) : (
                      <div className="truncate text-sm font-semibold text-fg">
                        {k.name}
                      </div>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {k.active ? (
                        <Badge variant="ok" size="sm">active</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">inactive</Badge>
                      )}
                      {k.networkCount !== undefined ? (
                        <Badge variant="neutral" size="sm">
                          {k.networkCount} networks
                        </Badge>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void saveRename(k.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                        >
                          <Save size={10} />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => { setEditingId(null); setDraftName(""); }}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                        >
                          <X size={10} />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => beginRename(k.id, k.name)}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                        >
                          <Pencil size={10} />
                          Rename
                        </button>
                        {!k.active ? (
                          <button
                            type="button"
                            onClick={() => void onActivate(k.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                          >
                            Activate
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => void onRemove(k.id, k.name)}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:border-rose/40 hover:bg-rose/10 hover:text-rose"
                        >
                          <Trash2 size={10} />
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
